import { sql } from "drizzle-orm";
import { pgTable, text, varchar, uuid, timestamp, numeric, integer, date, pgEnum, boolean } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { clientGroups } from "./schema-crm.js";
import { deals } from "./schema.js";  // deals defined in main schema (cross-domain reference)

// ============================================================================
// PARTNER MANAGER (MODULE 04) - 3 tables
// Referral partner/introducer management and tracking
// Sprint 3 - P1/P2 Priority
// ============================================================================

// Partner Type Enum
export const partnerTypeEnum = pgEnum("partner_type", [
  "mortgage_broker",
  "accountant",
  "financial_planner",
  "lawyer",
  "real_estate_agent",
  "business_broker",
  "other"
]);

// Partner Tier Enum
export const partnerTierEnum = pgEnum("partner_tier", [
  "platinum",
  "gold",
  "silver",
  "bronze"
]);

// Partner Status Enum
export const partnerStatusEnum = pgEnum("partner_status", [
  "active",
  "inactive",
  "suspended"
]);

// 9. Partners/Referral Sources Table (P1 - Sprint 3)
// Note: Using existing database schema structure with PRD-aligned commission fields
export const partners = pgTable("crm_x_partners", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerName: varchar("partner_name", { length: 255 }).notNull(),
  partnerType: varchar("partner_type", { length: 50 }),
  partnerTier: partnerTierEnum("partner_tier"),
  email: varchar("email", { length: 255 }),
  phone: varchar("phone", { length: 20 }),
  mobile: varchar("mobile", { length: 20 }),
  streetAddress: text("street_address"),
  suburb: varchar("suburb", { length: 100 }),
  state: varchar("state", { length: 50 }),
  postcode: varchar("postcode", { length: 10 }),
  abn: varchar("abn", { length: 11 }),
  companyName: varchar("company_name", { length: 255 }),
  website: varchar("website", { length: 255 }),

  // Commission fields (split upfront/trail per PRD)
  upfrontCommissionRate: numeric("upfront_commission_rate", { precision: 5, scale: 2 }).default("20.00"),
  trailCommissionRate: numeric("trail_commission_rate", { precision: 5, scale: 2 }).default("20.00"),
  paymentTermsDays: integer("payment_terms_days").default(7),
  commissionNotes: text("commission_notes"),

  // RCTI fields
  rctiRecipientName: varchar("rcti_recipient_name", { length: 255 }),
  rctiEmail: varchar("rcti_email", { length: 255 }),
  gstRegistered: boolean("gst_registered").default(true),

  // Legacy fields (kept for backward compatibility)
  defaultCommissionRate: numeric("default_commission_rate", { precision: 5, scale: 2 }),
  commissionPaymentTerms: varchar("commission_payment_terms", { length: 100 }),
  isActive: boolean("is_active").default(true),

  // Xero and linking
  xeroContactId: varchar("xero_contact_id", { length: 100 }),
  contactId: uuid("contact_id"),
  accountId: uuid("account_id"),

  // Status and metadata
  status: partnerStatusEnum("status").default("active"),
  partnerSince: date("partner_since").defaultNow(),
  notes: text("notes"),

  organisationId: uuid("organisation_id"),
  assignedBrokerId: uuid("assigned_broker_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

// 10. Partner Referrers Table (P2 - Sprint 3)
// Individual contacts within partner organizations
export const partnerReferrers = pgTable("crm_x_partner_referrers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: uuid("partner_id").references(() => partners.id, { onDelete: "cascade" }).notNull(),

  // Personal Information
  firstName: varchar("first_name", { length: 100 }).notNull(),
  lastName: varchar("last_name", { length: 100 }).notNull(),
  position: varchar("position", { length: 255 }),

  // Contact Details
  email: varchar("email", { length: 255 }),
  mobilePhone: varchar("mobile_phone", { length: 50 }),
  officePhone: varchar("office_phone", { length: 50 }),

  // Status
  isPrimaryContact: boolean("is_primary_contact").default(false),
  isActive: boolean("is_active").default(true),

  // Metadata
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedBy: uuid("updated_by"),
});

// 11. Referrals Table (P2 - Sprint 3)
// Track leads/referrals from partners
export const referrals = pgTable("crm_x_referrals", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  partnerId: uuid("partner_id").references(() => partners.id, { onDelete: "set null" }),
  partnerReferrerId: uuid("partner_referrer_id").references(() => partnerReferrers.id, { onDelete: "set null" }),
  dealId: uuid("deal_id").references(() => deals.id, { onDelete: "set null" }),

  // Referral Details
  referralDate: date("referral_date").notNull(),
  referralValue: numeric("referral_value", { precision: 15, scale: 2 }), // Estimated/actual deal value
  commissionPaid: numeric("commission_paid", { precision: 15, scale: 2 }),
  commissionDueDate: date("commission_due_date"),
  commissionPaidDate: date("commission_paid_date"),

  // Status
  referralStatus: varchar("referral_status", { length: 50 }).default("new"),

  // Metadata
  notes: text("notes"),
  assignedBrokerId: uuid("assigned_broker_id"),
  organisationId: uuid("organisation_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  createdBy: uuid("created_by"),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedBy: uuid("updated_by"),
});

// 12. Audit Log Table (Track major data changes and migrations)
export const auditActionEnum = pgEnum("audit_action", ["client_group_migration", "entity_created", "entity_updated", "entity_deleted"]);

export const auditLog = pgTable("crm_x_audit_log", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  action: auditActionEnum("action").notNull(),
  entityType: varchar("entity_type", { length: 50 }).notNull(), // contact, account, deal, etc.
  entityId: uuid("entity_id").notNull(),
  oldValue: text("old_value"), // JSON string of old state
  newValue: text("new_value"), // JSON string of new state
  changeDescription: text("change_description"),
  performedBy: uuid("performed_by"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  // Specific fields for client group migrations
  oldClientGroupId: uuid("old_client_group_id").references(() => clientGroups.id),
  newClientGroupId: uuid("new_client_group_id").references(() => clientGroups.id),
  migrationBatchId: uuid("migration_batch_id"), // Group all records from same migration operation
});

// ============================================================================
// PARTNER MANAGER ZOD SCHEMAS
// ============================================================================

// Partner Manager Module Schemas
export const insertPartnerSchema = createInsertSchema(partners, {
  partnerName: z.string().min(1, "Partner name is required"),
  abn: z
    .string()
    .optional()
    .nullable()
    .refine(
      (val) => {
        if (!val) return true; // Allow null/empty
        const digits = val.replace(/\s/g, ""); // Strip spaces
        return digits.length === 11 && /^\d+$/.test(digits);
      },
      { message: "ABN must be exactly 11 digits" }
    )
    .transform((val) => {
      // Strip spaces before storing
      if (!val) return val;
      return val.replace(/\s/g, "");
    }),
  website: z
    .string()
    .url("Must be a valid URL")
    .optional()
    .nullable()
    .or(z.literal("")),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  // Note: partners table doesn't have updatedBy column
});
export const updatePartnerSchema = insertPartnerSchema.partial();
export const selectPartnerSchema = createSelectSchema(partners);

export const insertPartnerReferrerSchema = createInsertSchema(partnerReferrers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true
});
export const updatePartnerReferrerSchema = insertPartnerReferrerSchema.partial();
export const selectPartnerReferrerSchema = createSelectSchema(partnerReferrers);

export const insertReferralSchema = createInsertSchema(referrals).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  createdBy: true,
  updatedBy: true
});
export const updateReferralSchema = insertReferralSchema.partial();
export const selectReferralSchema = createSelectSchema(referrals);

// Audit Log Schemas
export const insertAuditLogSchema = createInsertSchema(auditLog).omit({
  id: true,
  createdAt: true
});
export const updateAuditLogSchema = insertAuditLogSchema.partial();
export const selectAuditLogSchema = createSelectSchema(auditLog);

// ============================================================================
// PARTNER MANAGER TYPES
// ============================================================================

// Partner Manager Types
export type InsertPartner = z.infer<typeof insertPartnerSchema>;
export type UpdatePartner = z.infer<typeof updatePartnerSchema>;
export type Partner = typeof partners.$inferSelect;

export type InsertPartnerReferrer = z.infer<typeof insertPartnerReferrerSchema>;
export type UpdatePartnerReferrer = z.infer<typeof updatePartnerReferrerSchema>;
export type PartnerReferrer = typeof partnerReferrers.$inferSelect;

export type InsertReferral = z.infer<typeof insertReferralSchema>;
export type UpdateReferral = z.infer<typeof updateReferralSchema>;
export type Referral = typeof referrals.$inferSelect;

// Audit Log Types
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type UpdateAuditLog = z.infer<typeof updateAuditLogSchema>;
export type AuditLog = typeof auditLog.$inferSelect;
